import { Button } from '@strata/react-buttons';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { SuccessToast } from '@strata/react-toast-message';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { OrtfInboundFileDetailsView, OrtfInboundFilesGridSection } from './OrtfInboundFilesTab';
import { OrtfOutboundFileDetailsTab } from './OrtfOutboundFileDetailsTab';
import { OrtfOutboundGridSection } from './OrtfOutboundGridSection';

import { handleError } from 'services/Error';
import {
    useLazyGetRemainingTransfersBatchQuery,
    usePlanOutboundFilesMutation,
} from 'store/api/ndpApi';

const outboundSortDefaults = [{ id: 'pharmacy', desc: false }];
const OUTBOUND_FILES_INITIAL_EMPTY_MESSAGE = 'No outbound files generated.';
const OUTBOUND_FILES_NO_RESULTS_EMPTY_MESSAGE =
    'There are currently no outbound files that need generated.';

export const OrtfOutboundFilesTabV2 = ({
    generatedPlan = undefined,
    onGeneratedPlanChange,
    onRemainingTransfersBreakdownChange,
}) => {
    const [selectedOutboundFile, setSelectedOutboundFile] = useState(null);
    const [selectedInboundFile, setSelectedInboundFile] = useState(null);
    const [planOutboundFiles, { isLoading: isGenerating }] = usePlanOutboundFilesMutation();
    const [fetchRemainingTransfers, { isFetching: isFetchingRemainingTransfers }] =
        useLazyGetRemainingTransfersBatchQuery();

    const displayOutboundFiles = generatedPlan?.files ?? [];
    const isBusy = isGenerating || isFetchingRemainingTransfers;

    const handleGenerateOutboundFiles = async () => {
        try {
            const result = await planOutboundFiles().unwrap();
            onGeneratedPlanChange({
                files: result.files,
                remainingTransfersId: result.remainingTransfersId,
            });

            const remainingTransfers = await fetchRemainingTransfers(
                result.remainingTransfersId,
            ).unwrap();
            onRemainingTransfersBreakdownChange(remainingTransfers);

            SuccessToast({ message: 'Outbound files generated successfully.' });
        } catch (err) {
            handleError('Unable to generate outbound files. Please try again.', err);
        }
    };

    const handleOutboundFileRowClick = (row) => {
        const outboundFileId = row?.outboundFileId ?? row?.id;
        if (!outboundFileId) {
            handleError('Unable to open scripts for this file (missing outbound file id).');
            return;
        }

        setSelectedOutboundFile(row);
    };

    const outboundColumns = [
        {
            accessorKey: 'pharmacy',
            header: 'Pharmacy',
            size: 180,
        },
        {
            accessorKey: 'payer',
            header: 'Payer',
            size: 160,
        },
        {
            accessorKey: 'prescriptions',
            header: 'Prescriptions',
            size: 120,
        },
        {
            accessorKey: 'fileName',
            header: 'File Name',
            size: 200,
        },
    ];

    if (selectedInboundFile) {
        return (
            <OrtfInboundFileDetailsView
                selectedInboundFile={selectedInboundFile}
                onBack={() => setSelectedInboundFile(null)}
            />
        );
    }

    if (selectedOutboundFile) {
        return (
            <OrtfOutboundFileDetailsTab
                backLabel="File Processing"
                onBack={() => setSelectedOutboundFile(null)}
                onFileSent={(fileId) => {
                    onGeneratedPlanChange({
                        ...generatedPlan,
                        files: generatedPlan.files.filter((row) => row.id !== fileId),
                    });
                    setSelectedOutboundFile(null);
                }}
                outboundFile={selectedOutboundFile}
            />
        );
    }

    return (
        <div className="padding__around--medium">
            <FullscreenLoading isShowing={isBusy} loadingText="Generating outbound files..." />
            <OrtfOutboundGridSection
                actions={
                    <Button
                        intent={Button.Intent.PRIMARY}
                        label="Generate Outbound Files"
                        disabled={isBusy || generatedPlan !== undefined}
                        onClick={handleGenerateOutboundFiles}
                    />
                }
                columns={outboundColumns}
                data={displayOutboundFiles}
                onRowClick={handleOutboundFileRowClick}
                sortProperties={outboundSortDefaults}
                title="Outbound Files"
                emptyMessage={
                    generatedPlan !== undefined
                        ? OUTBOUND_FILES_NO_RESULTS_EMPTY_MESSAGE
                        : OUTBOUND_FILES_INITIAL_EMPTY_MESSAGE
                }
            />
            <OrtfInboundFilesGridSection
                className="margin__top--large"
                onRowClick={setSelectedInboundFile}
            />
        </div>
    );
};

const outboundFilePropType = PropTypes.shape({
    fileName: PropTypes.string,
    id: PropTypes.string,
    outboundFileId: PropTypes.string,
    payer: PropTypes.string,
    pharmacy: PropTypes.string,
    prescriptions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

OrtfOutboundFilesTabV2.propTypes = {
    generatedPlan: PropTypes.shape({
        files: PropTypes.arrayOf(outboundFilePropType).isRequired,
        remainingTransfersId: PropTypes.string,
    }),
    onGeneratedPlanChange: PropTypes.func.isRequired,
    onRemainingTransfersBreakdownChange: PropTypes.func.isRequired,
};
